import React, { useMemo } from 'react'
import { capitalize } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { DataListItem } from '@node-space/ui-components'
import { Country } from 'types/authentication/Profile'
import { BusinessCustomerType } from 'types/customers'
import { getNestedCustomerAddress } from '../utils'

interface BusinessCustomerSidepanelViewProps {
  customer: BusinessCustomerType
  countries: Country[]
  isCountriesLoading: boolean
  isCountriesLoadingError: boolean
}

export const BusinessCustomerSidepanelView = ({
  customer,
  countries,
  isCountriesLoading,
  isCountriesLoadingError,
}: BusinessCustomerSidepanelViewProps) => {
  const { t } = useTranslation()

  const taxResidencyCountry = useMemo(() => {
    return countries?.find(country => {
      if (country?.code === customer?.company?.taxResidenceCountryCode) {
        return country
      }
    })
  }, [countries, customer])

  return (
    <>
      {customer?.type && (
        <DataListItem
          id="type"
          testId="type"
          title={t('customers.type')}
          body={capitalize(customer?.type?.toLowerCase())}
        />
      )}
      {customer?.company?.name && (
        <DataListItem
          id="business-name"
          testId="business-name"
          title={t('customers.businessName')}
          body={customer?.company?.name}
        />
      )}
      {customer?.company?.industry?.name && (
        <DataListItem
          id="industry"
          testId="industry"
          title={t('customers.industry')}
          body={customer?.company?.industry?.name}
        />
      )}
      {customer?.company?.registrationNumber && (
        <DataListItem
          id="registration-number"
          testId="registration-number"
          title={t('customers.businessRegistrationNumber')}
          body={customer?.company?.registrationNumber}
        />
      )}
      {customer?.company?.taxResidenceCountryCode &&
        !isCountriesLoadingError &&
        !isCountriesLoading && (
          <DataListItem
            id="tax-residency-country"
            testId="tax-residency-country"
            title={t('customers.businessTaxResidency')}
            body={taxResidencyCountry?.name || ''}
          />
        )}
      {customer?.company?.address?.addressLine1 && (
        <DataListItem
          id="address"
          testId="address"
          title={t('customers.address.address')}
          body={getNestedCustomerAddress(customer?.company?.address)}
        />
      )}
      {customer?.company?.address?.countryCode && (
        <DataListItem
          id="country"
          testId="country"
          title={t('customers.country')}
          body={customer?.company?.address?.country}
        />
      )}
      <h4 className="text-h4 text-foreground-prominent">{t('customers.representative.title')}</h4>
      {customer?.company?.representative?.firstName && (
        <DataListItem
          id="representative-name"
          testId="representative-name"
          title={t('customers.representativeName')}
          body={`${customer?.company?.representative?.firstName} ${customer?.company?.representative?.lastName}`}
        />
      )}
      {customer?.company?.representative?.dateOfBirth && (
        <DataListItem
          id="date-of-birth"
          testId="date-of-birth"
          title={t('customers.representative.dateOfBirth')}
          body={customer?.company?.representative?.dateOfBirth}
        />
      )}
      {customer?.company?.representative?.address?.country && (
        <DataListItem
          id="country"
          testId="country"
          title={t('customers.representative.country')}
          body={customer?.company?.representative?.address?.country}
        />
      )}
    </>
  )
}
